<script setup>
/**
 * Full-screen sub-client picker for client users with `sub_clients`.
 * Cannot be dismissed without picking — forces an explicit choice.
 * The modal is presentational: it fetches each candidate's ClientsV2 doc
 * to render rich cards, but persistence/side-effects are left to the caller.
 */
import { ref, watch } from 'vue'
import { getDocument } from '@core/firebase/firestore.js'
import LoadingSpinner from '../LoadingSpinner.vue'

const props = defineProps({
  open: { type: Boolean, default: false },
  mainMakat: { type: String, default: null },
  candidates: { type: Array, default: () => [] }, // [{ makat, name }]
})

const emit = defineEmits(['selected', 'close'])

const isLoading = ref(false)
const clients = ref([])
const selectingMakat = ref(null)

async function loadCandidates() {
  isLoading.value = true
  clients.value = []
  try {
    const fetches = (props.candidates || []).map(async (c) => {
      const makat = String(c?.makat || '')
      if (!makat) return null
      try {
        const doc = await getDocument('ClientsV2', makat)
        if (!doc) return null
        return { ...doc, makat, _fallbackName: String(c?.name || '') }
      } catch {
        return null
      }
    })
    const results = await Promise.all(fetches)
    const valid = results.filter(Boolean)

    // Sort: main client first, then by name
    const main = props.mainMakat ? String(props.mainMakat) : null
    valid.sort((a, b) => {
      if (a.makat === main && b.makat !== main) return -1
      if (b.makat === main && a.makat !== main) return 1
      return String(a.name || '').localeCompare(String(b.name || ''), 'he')
    })
    clients.value = valid

    // Auto-resolve when only one valid candidate remains
    if (valid.length === 1) {
      autoSelect(valid[0])
    }
  } finally {
    isLoading.value = false
  }
}

function autoSelect(c) {
  emit('selected', {
    makat: c.makat,
    name: c.name || c._fallbackName || '',
    city: c.city || '',
    address: c.address || '',
    phone: c.phone || '',
  })
}

function selectClient(c) {
  if (selectingMakat.value) return
  selectingMakat.value = c.makat
  emit('selected', {
    makat: c.makat,
    name: c.name || c._fallbackName || '',
    city: c.city || '',
    address: c.address || '',
    phone: c.phone || '',
  })
}

function formatAddress(c) {
  const city = String(c?.city || '').trim()
  const street = String(c?.address || '').trim()
  if (city && street) return `${city}, ${street}`
  return city || street || '—'
}

watch(
  () => props.open,
  (isOpen) => {
    if (isOpen) {
      selectingMakat.value = null
      loadCandidates()
    }
  },
  { immediate: true }
)
</script>

<template>
  <Teleport to="body">
    <Transition name="client-picker">
      <div v-if="open" class="client-picker-overlay" role="dialog" aria-modal="true">
        <div class="client-picker-panel">
          <header class="client-picker-header">
            <h1 class="client-picker-title">בחירת לקוח</h1>
            <p class="client-picker-subtitle">בחר את הלקוח שאיתו תרצה לעבוד בכניסה זו</p>
          </header>

          <div class="client-picker-body">
            <div v-if="isLoading" class="client-picker-loading">
              <LoadingSpinner size="lg" />
              <span>טוען לקוחות...</span>
            </div>

            <div v-else-if="clients.length === 0" class="client-picker-empty">
              <p>לא נמצאו לקוחות זמינים</p>
            </div>

            <div v-else class="client-cards">
              <button
                v-for="c in clients"
                :key="c.makat"
                class="client-card"
                :class="{ 'is-main': String(c.makat) === String(mainMakat) }"
                :disabled="selectingMakat !== null"
                @click="selectClient(c)"
              >
                <svg
                  v-if="String(c.makat) === String(mainMakat)"
                  class="main-star"
                  viewBox="0 0 24 24"
                  width="22"
                  height="22"
                  fill="currentColor"
                  aria-label="לקוח ראשי"
                >
                  <path
                    d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"
                  />
                </svg>

                <div class="card-name">{{ c.name || c._fallbackName || 'ללא שם' }}</div>
                <div class="card-makat">מק"ט {{ c.makat }}</div>
                <div class="card-address">{{ formatAddress(c) }}</div>
                <div v-if="c.phone" class="card-phone">{{ c.phone }}</div>

                <div v-if="selectingMakat === c.makat" class="card-selecting">
                  <LoadingSpinner size="sm" />
                </div>
              </button>
            </div>
          </div>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.client-picker-overlay {
  position: fixed;
  inset: 0;
  z-index: 10000;
  background: var(--bg-secondary);
  display: flex;
  align-items: stretch;
  justify-content: center;
  overflow-y: auto;
}

.client-picker-panel {
  width: 100%;
  max-width: 1200px;
  padding: var(--spacing-xl) var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xl);
}

.client-picker-header {
  text-align: center;
}

.client-picker-title {
  margin: 0 0 var(--spacing-xs);
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
}

.client-picker-subtitle {
  margin: 0;
  font-size: var(--font-size-base);
  color: var(--text-secondary);
}

.client-picker-body {
  flex: 1;
}

.client-picker-loading,
.client-picker-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-2xl);
  color: var(--text-secondary);
}

.client-cards {
  display: grid;
  gap: var(--spacing-lg);
  grid-template-columns: 1fr;
}

@media (min-width: 768px) {
  .client-cards {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (min-width: 1280px) {
  .client-cards {
    grid-template-columns: repeat(3, 1fr);
  }
}

.client-card {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  padding: var(--spacing-lg);
  min-height: 160px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-sm);
  text-align: right;
  font-family: var(--font-family);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.client-card:hover:not(:disabled) {
  border-color: var(--color-primary);
  box-shadow: var(--shadow-md);
  transform: translateY(-2px);
}

.client-card:active:not(:disabled) {
  transform: translateY(0);
}

.client-card:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.client-card.is-main {
  border-color: var(--color-warning);
  box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
}

.main-star {
  position: absolute;
  top: var(--spacing-md);
  left: var(--spacing-md);
  color: var(--color-warning);
}

.card-name {
  font-size: var(--font-size-lg);
  font-weight: var(--font-weight-bold);
  color: var(--text-primary);
  padding-left: 32px;
  line-height: 1.3;
}

.card-makat {
  display: inline-block;
  padding: 2px 10px;
  margin-top: 4px;
  background: var(--bg-tertiary);
  color: var(--text-secondary);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.card-address {
  margin-top: 4px;
  font-size: var(--font-size-sm);
  color: var(--text-secondary);
  line-height: 1.4;
}

.card-phone {
  font-size: var(--font-size-xs);
  color: var(--text-muted);
  direction: ltr;
}

.card-selecting {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.7);
  border-radius: var(--border-radius-lg);
}

.client-picker-enter-active,
.client-picker-leave-active {
  transition: opacity 0.2s ease;
}

.client-picker-enter-from,
.client-picker-leave-to {
  opacity: 0;
}
</style>
